import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { first, map, switchMap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { AppTranslateService } from './translate.service';
import { UserService } from './user.service';

@Injectable({ providedIn: 'root' })
export class UserGuard {
  constructor(
    private authService: AuthService,
    private router: Router,
    private translateService: AppTranslateService,
    private userService: UserService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    console.log('UserGuard: Checking user authentication and settings...');

    return this.authService.authStateResolved$.pipe(
      first(resolved => resolved === true), // Wait for auth state to resolve
      switchMap(() => {
        return this.authService.currentUser$.pipe(
          first(),
          switchMap(user => {
            // Allow navigation if user is currently touring
            if (this.userService.touring) {
              console.log('UserGuard: User is touring, allowing navigation');
              return of(true);
            }
            
            if (!user) {
              console.warn('UserGuard: No user found. Redirecting to /login.');
              return of(this.router.createUrlTree(['/login']));
            }
            
            // Check if existing user is fresh (missing required settings)
            if (this.isFreshFirebaseUser(user)) {
              console.warn('UserGuard: Existing fresh user detected. Redirecting to /beginning.');
              return of(this.router.createUrlTree(['/beginning']));
            }
            
            // Check if user has languages but hasn't completed tour
            if (!user.completedTour && user.nativeLanguage && user.targetLanguage) {
              console.warn('UserGuard: User has languages but tour not completed. Redirecting to /welcome.');
              return of(this.router.createUrlTree(['/welcome']));
            }
            
            //console.log('UserGuard: User passed all checks. Access granted.');
            return of(true);
          })
        );
      })
    );
  }

  private isFreshFirebaseUser(user: any): boolean {
    if (!user) return false;

    const hasNativeLanguage = !!user.nativeLanguage;
    const hasTargetLanguage = !!user.targetLanguage;
    const hasLevel = user.level && Object.keys(user.level).length > 0;

    // If any of these fields are missing, consider the user "fresh"
    return !hasNativeLanguage || !hasTargetLanguage || !hasLevel;
  }
}